/**
 * Photograph a document (driving licence) at the desk.
 *
 * Uses the device camera when the browser allows it — a webcam on the desk PC,
 * the rear camera on a tablet — and always offers a file picker as well, so a
 * blocked camera permission or a scanner workflow never stops a check-in.
 * `capture="environment"` makes the picker open the camera directly on phones.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

const MAX_EDGE = 1800;     // plenty to read a licence, keeps uploads ~300–600 KB
const JPEG_QUALITY = 0.88;

function drawToJpeg(source, sw, sh) {
  const scale = Math.min(1, MAX_EDGE / Math.max(sw, sh));
  const c = document.createElement('canvas');
  c.width = Math.round(sw * scale);
  c.height = Math.round(sh * scale);
  c.getContext('2d').drawImage(source, 0, 0, c.width, c.height);
  return new Promise((resolve) => c.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
}

/** Downscale large phone photos before upload; leaves PDFs / HEIC untouched. */
async function normaliseImage(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return file;
  try {
    const bmp = await createImageBitmap(file);
    if (Math.max(bmp.width, bmp.height) <= MAX_EDGE && file.size < 1.5e6) return file;
    const blob = await drawToJpeg(bmp, bmp.width, bmp.height);
    return blob ? new File([blob], (file.name || 'capture').replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file;
  } catch {
    return file;
  }
}

export default function CameraCapture({ label, value, onChange, disabled = false, required = false }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [live, setLive] = useState(false);
  const [camError, setCamError] = useState('');

  // Object URL for the thumbnail, revoked when it changes.
  const preview = useMemo(() => (value ? URL.createObjectURL(value) : null), [value]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const stop = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLive(false);
  };
  useEffect(() => stop, []);

  useEffect(() => {
    if (live && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [live]);

  const openCamera = async () => {
    setCamError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamError('Camera not available in this browser — upload a photo instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      setLive(true);   // the effect below attaches the stream once <video> mounts
    } catch (e) {
      setCamError(e?.name === 'NotAllowedError'
        ? 'Camera permission was refused — upload a photo instead.'
        : 'Could not open the camera — upload a photo instead.');
    }
  };

  const snap = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const blob = await drawToJpeg(v, v.videoWidth, v.videoHeight);
    stop();
    if (blob) onChange(new File([blob], `${label.toLowerCase().replace(/\W+/g, '-')}.jpg`, { type: 'image/jpeg' }));
  };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) onChange(await normaliseImage(f));
  };

  return (
    <div className="form-field">
      <label>{label}{required && ' *'}</label>
      <div style={{
        border: '1px solid var(--border-color)', borderRadius: 8, padding: 10,
        display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--bg-glass)',
      }}>
        {live ? (
          <>
            <video ref={videoRef} playsInline muted
                   style={{ width: '100%', borderRadius: 6, background: '#000', aspectRatio: '16 / 10', objectFit: 'cover' }} />
            <div className="mono text-xs text-muted">Fill the frame with the card, avoid glare.</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn small" onClick={stop}>Cancel</button>
              <button type="button" className="btn small primary" style={{ flex: 1 }} onClick={snap}>📸 Take photo</button>
            </div>
          </>
        ) : preview ? (
          <>
            {value?.type === 'application/pdf'
              ? <div className="mono text-xs">📄 {value.name}</div>
              : <img src={preview} alt={label} style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 6, background: '#000' }} />}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn small" disabled={disabled} onClick={() => onChange(null)}>Retake</button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn small primary" disabled={disabled} onClick={openCamera}>📷 Use camera</button>
            <label className="btn small" style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
              ⬆ Upload photo
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                     capture="environment" hidden disabled={disabled} onChange={pick} />
            </label>
          </div>
        )}
        {camError && <div className="text-xs" style={{ color: 'var(--warning, #b7791f)' }}>{camError}</div>}
      </div>
    </div>
  );
}
